import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp, CircleDollarSign, Lock } from "lucide-react";
import { useTheme } from "@/App";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/components/AuthProvider";
import { useSubscription } from "@/hooks/useSubscription";

// ─── Types ────────────────────────────────────────────────────────────────────

export type Country = "US" | "CA" | "UK" | "FR" | "DE" | "AU" | "NL" | "CUSTOM" | "NONE";

export type TaxInputs = {
  country: Country;
  entityType: "1099" | "S-Corp";
  filingStatus: "Single" | "Married" | "Single with Dependent";
  state: string;
  grossIncome: string;
  salary: string;
  quarterlyEnabled: boolean;
  quarterly: { q1: string; q2: string; q3: string; q4: string };
  deductions: {
    homeOffice:      { enabled: boolean; percentage: string; monthlyRent: string };
    equipment:       { enabled: boolean; amount: string };
    healthInsurance: { enabled: boolean; amount: string };
    retirement:      { enabled: boolean; amount: string };
  };
};

export type TaxResults = {
  federalTax: number; stateTax: number; seTax: number;
  totalDeductions: number; quarterlyPaid: number; reserveNeeded: number;
  suggestedReservePct: number; underpaymentPenalty: number; totalTax: number;
};

type CustomConfig = {
  countryName: string;
  incomeTaxRate: string;
  socialChargesPct: string;
  standardDeduction: string;
  additionalExpensesPct: string;
  vatRegistered: boolean;
};

const DEFAULT_CUSTOM: CustomConfig = {
  countryName: "", incomeTaxRate: "", socialChargesPct: "",
  standardDeduction: "", additionalExpensesPct: "", vatRegistered: false,
};

// ─── Remote config types ──────────────────────────────────────────────────────

type TaxBracket = { upTo: number; rate: number };

type FRConfig = {
  social_charges: { rate: number; label: string; applied_to: string };
  income_allowance: number;
  brackets: TaxBracket[];
  notes: string;
};

type DEConfig = {
  grundfreibetrag: number;
  brackets: TaxBracket[];
  solidarity_surcharge: { rate: number; applies_above_tax_liability: number; label: string };
  trade_tax_exempt: boolean;
  notes: string;
};

type TaxConfig = {
  tax_year: string;
  us: {
    federalBrackets: TaxBracket[];
    standardDeductions: { Single: number; Married: number; "Single with Dependent": number };
    stateRates: Record<string, number>;
  };
  ca: {
    federalBrackets: TaxBracket[];
    basicPersonalAmount: number;
    provincialRates: Record<string, number>;
    corpFederalRate: number;
    corpProvincialRates: Record<string, number>;
    cpp: { rate: number; max: number };
    ei:  { rate: number; max: number };
  };
  uk: {
    personalAllowance: number; basicRateLimit: number; higherRateLimit: number;
    niLowerThreshold: number; niUpperThreshold: number;
    class4NiMainRate: number; class4NiUpperRate: number;
    corpTaxRate: number; corpSmallRate: number; corpSmallLimit: number;
    divBasicRate: number; divHigherRate: number; divAddlRate: number; divAllowance: number;
  };
  fr?: FRConfig;
  de?: DEConfig;
};

// ─── US Tax Data (2024) ───────────────────────────────────────────────────────

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming",
] as const;

const US_FEDERAL_BRACKETS = [
  { upTo: 11600,                    rate: 0.10 },
  { upTo: 47150,                    rate: 0.12 },
  { upTo: 100525,                   rate: 0.22 },
  { upTo: 191950,                   rate: 0.24 },
  { upTo: 243725,                   rate: 0.32 },
  { upTo: 609350,                   rate: 0.35 },
  { upTo: Number.POSITIVE_INFINITY, rate: 0.37 },
];

// 2024 Married Filing Jointly brackets
const US_MFJ_BRACKETS = [
  { upTo: 23200,                    rate: 0.10 },
  { upTo: 94300,                    rate: 0.12 },
  { upTo: 201050,                   rate: 0.22 },
  { upTo: 383900,                   rate: 0.24 },
  { upTo: 487450,                   rate: 0.32 },
  { upTo: 731200,                   rate: 0.35 },
  { upTo: Number.POSITIVE_INFINITY, rate: 0.37 },
];

// 2024 Head of Household brackets
const US_HOH_BRACKETS = [
  { upTo: 16550,                    rate: 0.10 },
  { upTo: 63100,                    rate: 0.12 },
  { upTo: 100500,                   rate: 0.22 },
  { upTo: 191950,                   rate: 0.24 },
  { upTo: 243700,                   rate: 0.32 },
  { upTo: 609350,                   rate: 0.35 },
  { upTo: Number.POSITIVE_INFINITY, rate: 0.37 },
];

const US_STATE_RATES: Record<string, number> = {
  Alabama: 0.05, Alaska: 0, Arizona: 0.025, Arkansas: 0.055, California: 0.093,
  Colorado: 0.044, Connecticut: 0.055, Delaware: 0.06, Florida: 0, Georgia: 0.0549,
  Hawaii: 0.0825, Idaho: 0.058, Illinois: 0.0495, Indiana: 0.0323, Iowa: 0.044,
  Kansas: 0.057, Kentucky: 0.045, Louisiana: 0.0425, Maine: 0.058, Maryland: 0.0575,
  Massachusetts: 0.05, Michigan: 0.0425, Minnesota: 0.0785, Mississippi: 0.05,
  Missouri: 0.0495, Montana: 0.0675, Nebraska: 0.0684, Nevada: 0,
  "New Hampshire": 0, "New Jersey": 0.0637, "New Mexico": 0.049, "New York": 0.0685,
  "North Carolina": 0.045, "North Dakota": 0.019, Ohio: 0.0399, Oklahoma: 0.0475,
  Oregon: 0.099, Pennsylvania: 0.0307, "Rhode Island": 0.0599, "South Carolina": 0.065,
  "South Dakota": 0, Tennessee: 0, Texas: 0, Utah: 0.0485, Vermont: 0.055,
  Virginia: 0.0575, Washington: 0, "West Virginia": 0.055, Wisconsin: 0.053, Wyoming: 0,
};

const US_STANDARD_DEDUCTIONS = {
  Single: 14600, Married: 29200, "Single with Dependent": 21900,
} as const;

// ─── Canada Tax Data (2024) ───────────────────────────────────────────────────

const CA_PROVINCES = [
  "Alberta","British Columbia","Manitoba","New Brunswick",
  "Newfoundland and Labrador","Northwest Territories","Nova Scotia","Nunavut",
  "Ontario","Prince Edward Island","Quebec","Saskatchewan","Yukon",
] as const;

const CA_FEDERAL_BRACKETS = [
  { upTo: 55867,                    rate: 0.15  },
  { upTo: 111733,                   rate: 0.205 },
  { upTo: 154906,                   rate: 0.26  },
  { upTo: 214368,                   rate: 0.29  },
  { upTo: Number.POSITIVE_INFINITY, rate: 0.33  },
];

const CA_PROVINCIAL_RATES: Record<string, number> = {
  "Alberta": 0.10, "British Columbia": 0.0853, "Manitoba": 0.1080,
  "New Brunswick": 0.1082, "Newfoundland and Labrador": 0.108,
  "Northwest Territories": 0.0905, "Nova Scotia": 0.1200, "Nunavut": 0.0400,
  "Ontario": 0.0953, "Prince Edward Island": 0.1050, "Quebec": 0.1375,
  "Saskatchewan": 0.0875, "Yukon": 0.0640,
};

const CA_CORP_FEDERAL_RATE = 0.09;
const CA_CORP_PROVINCIAL_RATES: Record<string, number> = {
  "Alberta": 0.02, "British Columbia": 0.02, "Manitoba": 0.00,
  "New Brunswick": 0.025, "Newfoundland and Labrador": 0.03,
  "Northwest Territories": 0.02, "Nova Scotia": 0.025, "Nunavut": 0.03,
  "Ontario": 0.035, "Prince Edward Island": 0.02, "Quebec": 0.035,
  "Saskatchewan": 0.02, "Yukon": 0.02,
};

const CA_BASIC_PERSONAL = 15705;
const CA_CPP_RATE = 0.0595; const CA_CPP_MAX = 68500;
const CA_EI_RATE  = 0.0166; const CA_EI_MAX  = 63200;

// ─── Netherlands Tax Data (2024) ─────────────────────────────────────────────
// ZZP (Zelfstandige Zonder Personeel) model

const NL_BRACKETS = [
  { upTo: 75518,                    rate: 0.3697 },
  { upTo: Number.POSITIVE_INFINITY, rate: 0.495  },
];

const NL_ZELFSTANDIGENAFTREK = 3750;  // self-employment deduction
const NL_MKB_EXEMPTION       = 0.1331; // SME profit exemption (after zelfstandigenaftrek)
const NL_HEFFINGSKORTING_MAX = 3362;  // general tax credit (2024)
const NL_HEFFINGSKORTING_PHASE_START = 24813; // income where credit starts phasing out
const NL_HEFFINGSKORTING_PHASE_RATE  = 0.0663; // phase-out rate (2024)

// ─── Australia Tax Data (2024/25) ────────────────────────────────────────────

const AU_TAX_BRACKETS = [
  { upTo: 18200,                    rate: 0     },
  { upTo: 45000,                    rate: 0.19  },
  { upTo: 135000,                   rate: 0.325 }, // Stage 3 cut: was $120k
  { upTo: 190000,                   rate: 0.37  }, // Stage 3 cut: was $180k
  { upTo: Number.POSITIVE_INFINITY, rate: 0.45  },
];

const AU_MEDICARE_RATE      = 0.02;
const AU_MEDICARE_THRESHOLD = 26000;
const AU_LITO_MAX           = 700;
const AU_CORP_RATE          = 0.25; // small business base rate entity

// ─── UK Tax Data (2024/25) ────────────────────────────────────────────────────

const UK_PERSONAL_ALLOWANCE = 12570;
const UK_BASIC_RATE_LIMIT   = 50270;
const UK_HIGHER_RATE_LIMIT  = 125140;
const UK_NI_LOWER_THRESHOLD = 12570;
const UK_NI_UPPER_THRESHOLD = 50270;
const UK_CORP_TAX_RATE      = 0.25;
const UK_CORP_SMALL_RATE    = 0.19;
const UK_CORP_SMALL_LIMIT   = 50000;
const UK_DIV_BASIC_RATE     = 0.0875;
const UK_DIV_HIGHER_RATE    = 0.3375;
const UK_DIV_ADDL_RATE      = 0.3935;
const UK_DIV_ALLOWANCE      = 500;

// ─── France / Germany defaults (fallback when Storage fetch lacks these keys) ─

const DEFAULT_FR: FRConfig = {
  social_charges: { rate: 0.231, label: "Social charges (on gross revenue)", applied_to: "gross" },
  income_allowance: 0.34,
  brackets: [
    { upTo: 11294,                    rate: 0    },
    { upTo: 28797,                    rate: 0.11 },
    { upTo: 82341,                    rate: 0.30 },
    { upTo: 177106,                   rate: 0.41 },
    { upTo: Number.POSITIVE_INFINITY, rate: 0.45 },
  ],
  notes: "Income tax applied after 34% BNC allowance. Social charges applied to gross revenue separately.",
};

const DEFAULT_DE: DEConfig = {
  grundfreibetrag: 11604,
  brackets: [
    { upTo: 11604,                    rate: 0    },
    { upTo: 17005,                    rate: 0.14 },
    { upTo: 66760,                    rate: 0.24 },
    { upTo: 277825,                   rate: 0.42 },
    { upTo: Number.POSITIVE_INFINITY, rate: 0.45 },
  ],
  solidarity_surcharge: {
    rate: 0.055,
    applies_above_tax_liability: 18130,
    label: "Solidarity surcharge (5.5% on income tax, most freelancers exempt)",
  },
  trade_tax_exempt: true,
  notes: "Freiberufler are exempt from Gewerbesteuer (trade tax). Private health insurance not modelled. Standard business expense deductions apply.",
};

// ─── Hardcoded defaults (fallback when Storage fetch fails) ──────────────────

const DEFAULT_TAX_CONFIG: TaxConfig = {
  tax_year: "2024",
  us: {
    federalBrackets: US_FEDERAL_BRACKETS.map(b => ({ upTo: b.upTo === Number.POSITIVE_INFINITY ? 1e308 : b.upTo, rate: b.rate })),
    standardDeductions: US_STANDARD_DEDUCTIONS,
    stateRates: US_STATE_RATES,
  },
  ca: {
    federalBrackets: CA_FEDERAL_BRACKETS.map(b => ({ upTo: b.upTo === Number.POSITIVE_INFINITY ? 1e308 : b.upTo, rate: b.rate })),
    basicPersonalAmount: CA_BASIC_PERSONAL,
    provincialRates: CA_PROVINCIAL_RATES,
    corpFederalRate: CA_CORP_FEDERAL_RATE,
    corpProvincialRates: CA_CORP_PROVINCIAL_RATES,
    cpp: { rate: CA_CPP_RATE, max: CA_CPP_MAX },
    ei:  { rate: CA_EI_RATE,  max: CA_EI_MAX  },
  },
  uk: {
    personalAllowance: UK_PERSONAL_ALLOWANCE, basicRateLimit: UK_BASIC_RATE_LIMIT,
    higherRateLimit: UK_HIGHER_RATE_LIMIT, niLowerThreshold: UK_NI_LOWER_THRESHOLD,
    niUpperThreshold: UK_NI_UPPER_THRESHOLD, class4NiMainRate: 0.06, class4NiUpperRate: 0.02,
    corpTaxRate: UK_CORP_TAX_RATE, corpSmallRate: UK_CORP_SMALL_RATE,
    corpSmallLimit: UK_CORP_SMALL_LIMIT, divBasicRate: UK_DIV_BASIC_RATE,
    divHigherRate: UK_DIV_HIGHER_RATE, divAddlRate: UK_DIV_ADDL_RATE, divAllowance: UK_DIV_ALLOWANCE,
  },
  fr: DEFAULT_FR,
  de: DEFAULT_DE,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Convert 1e308 sentinel back to POSITIVE_INFINITY for bracket math
function normaliseBrackets(brackets: TaxBracket[]): { upTo: number; rate: number }[] {
  return brackets.map(b => ({ ...b, upTo: b.upTo >= 1e307 ? Number.POSITIVE_INFINITY : b.upTo }));
}

const money = (value: string) => Number(value.replace(/[^0-9.-]/g, "")) || 0;

function fmtInput(value: string, symbol: string): string {
  const cleaned = value.replace(/[^0-9.-]/g, "");
  if (!cleaned) return "";
  const num = Number(cleaned);
  if (Number.isNaN(num)) return value;
  return symbol + Math.round(num).toLocaleString('en-US');
}

function calcBracketTax(income: number, brackets: { upTo: number; rate: number }[]): number {
  let remaining = Math.max(0, income), lastCap = 0, total = 0;
  for (const b of brackets) {
    const amount = Math.min(remaining, b.upTo - lastCap);
    if (amount > 0) total += amount * b.rate;
    remaining -= amount; lastCap = b.upTo;
    if (remaining <= 0) break;
  }
  return total;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function TaxesPage() {
  const { isDark, toggleTheme, currency: globalCurrency } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan, status } = useSubscription();
  const showLeadGate = plan === 'junior' && status === 'active';

  const initialCountry: Country = globalCurrency === "GBP" ? "UK" : "US";
  const initialState   = globalCurrency === "GBP" ? "" : "California";
  const initialSymbol  = globalCurrency === "GBP" ? "£" : "$";

  const savedYTD = localStorage.getItem("fh_ytd_earnings");
  const defaultGross = savedYTD && Number(savedYTD) > 0
    ? initialSymbol + Math.round(Number(savedYTD)).toLocaleString('en-US')
    : "";

  const [taxInputs, setTaxInputs] = useState<TaxInputs>({
    country: initialCountry,
    entityType: "1099",
    filingStatus: "Single",
    state: initialState,
    grossIncome: defaultGross,
    salary: "$80,000",
    quarterlyEnabled: false,
    quarterly: { q1: "", q2: "", q3: "", q4: "" },
    deductions: {
      homeOffice:      { enabled: false, percentage: "", monthlyRent: "" },
      equipment:       { enabled: false, amount: "" },
      healthInsurance: { enabled: false, amount: "" },
      retirement:      { enabled: false, amount: "" },
    },
  });

  const [showDeductions, setShowDeductions] = useState(false);

  // ─── Custom mode state ───────────────────────────────────────────────────
  const [customConfig, setCustomConfig] = useState<CustomConfig>(DEFAULT_CUSTOM);
  const customSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taxInputsSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [taxInputsLoaded, setTaxInputsLoaded] = useState(false);

  // ─── Remote tax config (fetched from Supabase Storage on mount) ─────────
  const [taxConfig, setTaxConfig] = useState<TaxConfig>(DEFAULT_TAX_CONFIG);
  const [taxYear,   setTaxYear]   = useState(DEFAULT_TAX_CONFIG.tax_year);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      const { data, error } = await supabase.storage.from('config').download('tax_config.json');
      if (error || !data) { console.warn('[taxes] Using fallback tax config:', error?.message); return; }
      try {
        const parsed: TaxConfig = JSON.parse(await data.text());
        setTaxConfig(parsed);
        setTaxYear(parsed.tax_year ?? DEFAULT_TAX_CONFIG.tax_year);
      } catch (e) { console.warn('[taxes] Failed to parse remote tax config, using fallback:', e); }
    })();
  }, []);

  // Computes live YTD earnings from started bookings and writes them into grossIncome.
  // Used on first-ever visit (no saved override yet) and by the "Use live YTD" reset button.
  const applyLiveYtdGrossIncome = useCallback(async () => {
    if (!user) return;
    const currentYear = new Date().getFullYear();
    const { data: bookingsData } = await supabase
      .from('bookings')
      .select('earnings, start_date')
      .eq('user_id', user.id)
      .eq('archived', false)
      .gte('start_date', `${currentYear}-01-01`)
      .lte('start_date', `${currentYear}-12-31`);

    const ytdEarnings = (bookingsData ?? [])
      .reduce((s, b) => s + (parseFloat(b.earnings) || 0), 0);

    const sym = taxInputs.country === 'UK' ? '£' : (taxInputs.country === 'FR' || taxInputs.country === 'DE' || taxInputs.country === 'NL') ? '€' : taxInputs.country === 'AU' ? 'A$' : '$';
    setTaxInputs(prev => ({ ...prev, grossIncome: ytdEarnings > 0 ? sym + Math.round(ytdEarnings).toLocaleString('en-US') : prev.grossIncome }));
  }, [user, taxInputs.country]);

  // ─── Load app_settings (custom_tax_config + tax_inputs) and YTD bookings on mount ─
  // grossIncome is a planning figure — once the user sets one (manually or via the live-YTD
  // button) it's persisted and restored as-is. We only auto-fill from live bookings the very
  // first time, when there's no saved value yet, so a mid-year projection isn't silently
  // overwritten by actual YTD earnings on every page reload.
  useEffect(() => {
    if (!user) return;
    (async () => {
      // Load persisted settings
      const { data } = await supabase
        .from('app_settings')
        .select('custom_tax_config, tax_inputs')
        .eq('user_id', user.id)
        .maybeSingle();
      if (data?.custom_tax_config) {
        setCustomConfig(prev => ({ ...prev, ...data.custom_tax_config }));
      }
      if (data?.tax_inputs) {
        const loaded = data.tax_inputs;
        if (loaded.country === "NONE") loaded.country = "US";
        setTaxInputs(prev => ({ ...prev, ...loaded }));
      }

      if (!data?.tax_inputs?.grossIncome) {
        await applyLiveYtdGrossIncome();
      }
      setTaxInputsLoaded(true);
    })();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Debounced save of customConfig to Supabase ──────────────────────────
  useEffect(() => {
    if (!user || taxInputs.country !== "CUSTOM") return;
    if (customSaveTimer.current) clearTimeout(customSaveTimer.current);
    customSaveTimer.current = setTimeout(async () => {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ user_id: user.id, custom_tax_config: customConfig }, { onConflict: 'user_id' });
      if (error) console.error('[taxes] custom_tax_config save failed:', error);
    }, 1000);
    return () => { if (customSaveTimer.current) clearTimeout(customSaveTimer.current); };
  }, [customConfig, user, taxInputs.country]);

  // ─── Debounced save of taxInputs to Supabase (grossIncome included — it's a planning
  // figure the user controls, not a live-only readout; see applyLiveYtdGrossIncome above) ──
  useEffect(() => {
    if (!user) return;
    if (taxInputsSaveTimer.current) clearTimeout(taxInputsSaveTimer.current);
    taxInputsSaveTimer.current = setTimeout(async () => {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ user_id: user.id, tax_inputs: taxInputs }, { onConflict: 'user_id' });
      if (error) console.error('[taxes] tax_inputs save failed:', error);
    }, 1000);
    return () => { if (taxInputsSaveTimer.current) clearTimeout(taxInputsSaveTimer.current); };
  }, [taxInputs, user]);

  // ─── Log country request on blur ─────────────────────────────────────────
  const handleCountryNameBlur = useCallback(async () => {
    if (!user || !customConfig.countryName.trim()) return;
    const { error } = await supabase
      .from('country_requests')
      .upsert(
        { user_id: user.id, country_name: customConfig.countryName.trim(), requested_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      );
    if (error) console.error('[taxes] country_requests upsert failed:', error); // silent to user
  }, [user, customConfig.countryName]);

  // ─── Currency symbol ─────────────────────────────────────────────────────
  const currencySymbol =
    taxInputs.country === "UK" ? "£" :
    taxInputs.country === "FR" || taxInputs.country === "DE" || taxInputs.country === "NL" ? "€" :
    taxInputs.country === "AU" ? "A$" :
    "$";
  const fmt = (value: number) => currencySymbol + Math.round(value).toLocaleString('en-US');

  function handleCountryChange(c: Country) {
    const rawAmount = money(taxInputs.grossIncome);
    const newSymbol = c === "UK" ? "£" : (c === "FR" || c === "DE" || c === "NL") ? "€" : c === "AU" ? "A$" : "$";
    const reformatted = rawAmount > 0 ? newSymbol + Math.round(rawAmount).toLocaleString('en-US') : "";
    setTaxInputs(p => ({
      ...p, country: c, grossIncome: reformatted,
      state: c === "CA" ? "Ontario" : c === "US" ? "California" : "",
      deductions: {
        homeOffice:      { enabled: false, percentage: "", monthlyRent: "" },
        equipment:       { enabled: false, amount: "" },
        healthInsurance: { enabled: false, amount: "" },
        retirement:      { enabled: false, amount: "" },
      },
    }));
  }

  // ─── Visibility flags ────────────────────────────────────────────────────
  const showEntityType  = taxInputs.country === "US" || taxInputs.country === "CA" || taxInputs.country === "UK" || taxInputs.country === "AU";
  const showFilingStatus= taxInputs.country === "US";
  const showState       = taxInputs.country === "US" || taxInputs.country === "CA";
  const showDeductionUI = taxInputs.country === "US" || taxInputs.country === "CA" || taxInputs.country === "UK" || taxInputs.country === "DE" || taxInputs.country === "AU";
  const showQuarterly   = taxInputs.country === "US" || taxInputs.country === "CA" || taxInputs.country === "UK" || taxInputs.country === "AU";
  const showGrossIncome = taxInputs.country !== "NONE";
  const showCustomForm  = taxInputs.country === "CUSTOM";
  const showNoneCard    = taxInputs.country === "NONE";

  const isCorpEntity = taxInputs.entityType === "S-Corp";

  const entityLabels =
    taxInputs.country === "CA" ? ["Sole Proprietor", "Corporation"] :
    taxInputs.country === "UK" ? ["Sole Trader", "Ltd Company"] :
    taxInputs.country === "AU" ? ["Sole Trader", "PTY Ltd"] :
    ["1099", "S-Corp"];

  const seLabel =
    taxInputs.country === "CA" ? (isCorpEntity ? "Corporate Tax (Fed + Provincial)" : "CPP / EI Contributions") :
    taxInputs.country === "UK" ? (isCorpEntity ? "Corporation Tax" : "National Insurance (Class 4)") :
    taxInputs.country === "AU" ? (isCorpEntity ? "Company Tax (25%)" : "Medicare Levy (2%)") :
    taxInputs.entityType === "1099" ? "SE Tax — 15.3% on net" : "SE Tax — 15.3% on salary";

  const stateTaxLabel =
    taxInputs.country === "CA" ? (isCorpEntity ? "Dividend / Salary Tax" : "Provincial Tax (estimated)") :
    taxInputs.country === "UK" ? (isCorpEntity ? "Dividend Tax" : "") :
    taxInputs.country === "AU" ? "" :
    "State Tax (estimated)";

  const stateList  = taxInputs.country === "CA" ? CA_PROVINCES : US_STATES;
  const stateLabel = taxInputs.country === "CA" ? "Province" : "State";

  // ─── Tax calculation ─────────────────────────────────────────────────────
  const taxResults = useMemo<TaxResults>(() => {
    const cfg = taxConfig;
    const grossIncome  = money(taxInputs.grossIncome);
    const quarterPaid  = Object.values(taxInputs.quarterly).reduce((s, v) => s + money(v), 0);
    const homeOffice   = taxInputs.deductions.homeOffice.enabled
      ? (Number(taxInputs.deductions.homeOffice.monthlyRent.replace(/[^0-9.-]/g, "")) || 0) * 12
        * ((Number(taxInputs.deductions.homeOffice.percentage) || 0) / 100) : 0;
    const equipment    = taxInputs.deductions.equipment.enabled     ? money(taxInputs.deductions.equipment.amount) : 0;
    const health       = taxInputs.deductions.healthInsurance.enabled ? money(taxInputs.deductions.healthInsurance.amount) : 0;
    const retirement   = taxInputs.deductions.retirement.enabled    ? money(taxInputs.deductions.retirement.amount) : 0;
    const totalDeductions = homeOffice + equipment + health + retirement;

    const zero: TaxResults = { federalTax: 0, stateTax: 0, seTax: 0, totalDeductions: 0, quarterlyPaid: 0, reserveNeeded: 0, suggestedReservePct: 0, underpaymentPenalty: 0, totalTax: 0 };

    // ── NONE ──
    if (taxInputs.country === "NONE") return zero;

    // ── CUSTOM ──
    if (taxInputs.country === "CUSTOM") {
      const stdDed      = money(customConfig.standardDeduction);
      const addlPct     = Number(customConfig.additionalExpensesPct) || 0;
      const addlDed     = grossIncome * (addlPct / 100);
      const totalDed    = stdDed + addlDed;
      const taxableInc  = Math.max(0, grossIncome - totalDed);
      const incomeTax   = taxableInc * ((Number(customConfig.incomeTaxRate) || 0) / 100);
      const socialChg   = grossIncome * ((Number(customConfig.socialChargesPct) || 0) / 100);
      const totalTax    = incomeTax + socialChg;
      const reservePct  = grossIncome > 0 ? Math.round((totalTax / grossIncome) * 100) : 0;
      return { federalTax: incomeTax, stateTax: 0, seTax: socialChg, totalDeductions: totalDed, quarterlyPaid: 0, reserveNeeded: totalTax, suggestedReservePct: reservePct, underpaymentPenalty: 0, totalTax };
    }

    // ── FRANCE ──
    if (taxInputs.country === "FR") {
      const fr = cfg.fr ?? DEFAULT_FR;
      const frBrackets  = normaliseBrackets(fr.brackets);
      const socialChg   = grossIncome * fr.social_charges.rate;
      const taxableInc  = Math.max(0, grossIncome * (1 - fr.income_allowance));
      const incomeTax   = calcBracketTax(taxableInc, frBrackets);
      const totalTax    = socialChg + incomeTax;
      const reservePct  = grossIncome > 0 ? Math.round((totalTax / grossIncome) * 100) : 0;
      return { federalTax: incomeTax, stateTax: 0, seTax: socialChg, totalDeductions: 0, quarterlyPaid: 0, reserveNeeded: totalTax, suggestedReservePct: reservePct, underpaymentPenalty: 0, totalTax };
    }

    // ── GERMANY ──
    if (taxInputs.country === "DE") {
      const de = cfg.de ?? DEFAULT_DE;
      const x = Math.max(0, grossIncome - totalDeductions);
      // BMF quadratic-progression formula (2024 Grundfreibetrag €11,604)
      let incomeTax = 0;
      if (x <= 11604) {
        incomeTax = 0;
      } else if (x <= 17005) {
        const y = (x - 11604) / 10000;
        incomeTax = (930.17 * y + 1400) * y;
      } else if (x <= 66760) {
        const z = (x - 17005) / 10000;
        incomeTax = (181.19 * z + 2397) * z + 1025.38;
      } else if (x <= 277825) {
        incomeTax = 0.42 * x - 10602.13;
      } else {
        incomeTax = 0.45 * x - 18936.88;
      }
      incomeTax = Math.round(incomeTax);
      // Solidarity surcharge with Milderungszone (soft phase-in above Freigrenze ~€17,543)
      const soliFreigrenze = 17543;
      const solidarity = incomeTax <= soliFreigrenze ? 0
        : Math.min(incomeTax * 0.055, 0.20 * (incomeTax - soliFreigrenze));
      const totalTax    = incomeTax + solidarity;
      const reserveNeeded = Math.max(0, totalTax - quarterPaid);
      const reservePct  = grossIncome > 0 ? Math.round((totalTax / grossIncome) * 100) : 0;
      return { federalTax: incomeTax, stateTax: solidarity, seTax: 0, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: reservePct, underpaymentPenalty: 0, totalTax };
    }

    // ── AUSTRALIA ──
    if (taxInputs.country === "AU") {
      const taxableIncome = Math.max(0, grossIncome - totalDeductions);
      if (!isCorpEntity) {
        const grossTax = calcBracketTax(taxableIncome, AU_TAX_BRACKETS);
        // Low Income Tax Offset (LITO)
        let lito = 0;
        if      (taxableIncome <= 37500) lito = AU_LITO_MAX;
        else if (taxableIncome <= 45000) lito = AU_LITO_MAX - (taxableIncome - 37500) * 0.05;
        else if (taxableIncome <= 66667) lito = 325 - (taxableIncome - 45000) * 0.015;
        const incomeTax = Math.max(0, grossTax - Math.max(0, lito));
        const medicare  = taxableIncome > AU_MEDICARE_THRESHOLD ? taxableIncome * AU_MEDICARE_RATE : 0;
        const totalTax  = incomeTax + medicare;
        const reserveNeeded = Math.max(0, totalTax - quarterPaid);
        const reservePct    = grossIncome > 0 ? Math.round((totalTax / grossIncome) * 100) : 0;
        return { federalTax: incomeTax, stateTax: medicare, seTax: 0, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: reservePct, underpaymentPenalty: 0, totalTax };
      } else {
        const companyTax = taxableIncome * AU_CORP_RATE;
        const reserveNeeded = Math.max(0, companyTax - quarterPaid);
        const reservePct    = grossIncome > 0 ? Math.round((companyTax / grossIncome) * 100) : 0;
        return { federalTax: 0, stateTax: 0, seTax: companyTax, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: reservePct, underpaymentPenalty: 0, totalTax: companyTax };
      }
    }

    // ── NETHERLANDS (ZZP) ──
    if (taxInputs.country === "NL") {
      // 1. Zelfstandigenaftrek (self-employment deduction)
      const zelfst      = Math.min(NL_ZELFSTANDIGENAFTREK, grossIncome);
      // 2. MKB-winstvrijstelling: 13.31% off profit after zelfstandigenaftrek + user deductions
      const afterZelfst = Math.max(0, grossIncome - zelfst - totalDeductions);
      const taxableIncome = afterZelfst * (1 - NL_MKB_EXEMPTION);
      // 3. Box 1 brackets
      const grossTax    = calcBracketTax(taxableIncome, NL_BRACKETS);
      // 4. Algemene heffingskorting (general tax credit, income-dependent)
      const heffing     = taxableIncome <= NL_HEFFINGSKORTING_PHASE_START
        ? NL_HEFFINGSKORTING_MAX
        : Math.max(0, NL_HEFFINGSKORTING_MAX - (taxableIncome - NL_HEFFINGSKORTING_PHASE_START) * NL_HEFFINGSKORTING_PHASE_RATE);
      const incomeTax   = Math.max(0, grossTax - heffing);
      const reserveNeeded = Math.max(0, incomeTax - quarterPaid);
      const reservePct    = grossIncome > 0 ? Math.round((incomeTax / grossIncome) * 100) : 0;
      return { federalTax: incomeTax, stateTax: zelfst + totalDeductions, seTax: 0, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: reservePct, underpaymentPenalty: 0, totalTax: incomeTax };
    }

    const caFedBrackets = normaliseBrackets(cfg.ca.federalBrackets);
    const usFedBrackets = normaliseBrackets(cfg.us.federalBrackets);

    // ── CANADA sole prop ──
    if (taxInputs.country === "CA" && !isCorpEntity) {
      const taxableIncome = Math.max(0, grossIncome - totalDeductions);
      // BPA is a non-refundable credit at the 15% lowest rate, not an income deduction
      const bpaCredit  = cfg.ca.basicPersonalAmount * 0.15;
      const federalTax = Math.max(0, calcBracketTax(taxableIncome, caFedBrackets) - bpaCredit);
      const stateTax   = taxableIncome * (cfg.ca.provincialRates[taxInputs.state] ?? 0.10);
      // Self-employed pay both employee + employer CPP halves (2× rate); $3,500 basic exemption applies
      const cppBase = Math.max(0, Math.min(grossIncome, cfg.ca.cpp.max) - 3500);
      const cpp     = cppBase * cfg.ca.cpp.rate * 2;
      // CPP2 (2024): enhanced second tier, 8% (4%+4%) on earnings $68,500–$73,200
      const cpp2Base = Math.max(0, Math.min(grossIncome, 73200) - 68500);
      const cpp2     = cpp2Base * 0.08;
      // EI is voluntary for self-employed; not charged here
      const seTax   = cpp + cpp2;
      const totalTax   = federalTax + stateTax + seTax;
      const reserveNeeded = Math.max(0, totalTax - quarterPaid);
      return { federalTax, stateTax, seTax, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: grossIncome > 0 ? Math.round((reserveNeeded / grossIncome) * 100) : 0, underpaymentPenalty: 0, totalTax };
    }

    // ── CANADA corp ──
    if (taxInputs.country === "CA" && isCorpEntity) {
      const corpProfit      = Math.max(0, grossIncome - totalDeductions);
      const seTax           = (corpProfit * cfg.ca.corpFederalRate) + (corpProfit * (cfg.ca.corpProvincialRates[taxInputs.state] ?? 0.02));
      const personalTaxable = Math.max(0, grossIncome * 0.5);
      const bpaCredit       = cfg.ca.basicPersonalAmount * 0.15;
      const federalTax      = Math.max(0, calcBracketTax(personalTaxable, caFedBrackets) - bpaCredit);
      const stateTax        = personalTaxable * (cfg.ca.provincialRates[taxInputs.state] ?? 0.10);
      const totalTax        = seTax + federalTax + stateTax;
      const reserveNeeded   = Math.max(0, totalTax - quarterPaid);
      return { federalTax, stateTax, seTax, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: grossIncome > 0 ? Math.round((reserveNeeded / grossIncome) * 100) : 0, underpaymentPenalty: 0, totalTax };
    }

    // ── UK sole trader ──
    if (taxInputs.country === "UK" && !isCorpEntity) {
      const uk = cfg.uk;
      const taxableIncome = Math.max(0, grossIncome - totalDeductions);
      // Personal Allowance tapers by £1 per £2 above £100,000, fully gone at £125,140
      const effectivePA = taxableIncome <= 100000
        ? uk.personalAllowance
        : Math.max(0, uk.personalAllowance - Math.floor((taxableIncome - 100000) / 2));
      const basic  = Math.min(Math.max(0, taxableIncome - effectivePA), uk.basicRateLimit - effectivePA) * 0.20;
      const higher = Math.min(Math.max(0, taxableIncome - uk.basicRateLimit), uk.higherRateLimit - uk.basicRateLimit) * 0.40;
      const addl   = Math.max(0, taxableIncome - uk.higherRateLimit) * 0.45;
      const federalTax = basic + higher + addl;
      const niLower = Math.min(Math.max(0, taxableIncome - uk.niLowerThreshold), uk.niUpperThreshold - uk.niLowerThreshold) * uk.class4NiMainRate;
      const niUpper = Math.max(0, taxableIncome - uk.niUpperThreshold) * uk.class4NiUpperRate;
      // Class 2 NI: £3.45/week flat for profits above Small Profits Threshold (£12,570)
      const class2Ni = taxableIncome > uk.niLowerThreshold ? 3.45 * 52 : 0;
      const seTax   = niLower + niUpper + class2Ni;
      const totalTax      = federalTax + seTax;
      const reserveNeeded = Math.max(0, totalTax - quarterPaid);
      return { federalTax, stateTax: 0, seTax, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: grossIncome > 0 ? Math.round((reserveNeeded / grossIncome) * 100) : 0, underpaymentPenalty: 0, totalTax };
    }

    // ── UK Ltd ──
    if (taxInputs.country === "UK" && isCorpEntity) {
      const uk = cfg.uk;
      const profit         = Math.max(0, grossIncome - totalDeductions);
      // Corp tax with HMRC marginal relief for profits £50k–£250k
      const seTax = profit <= uk.corpSmallLimit
        ? profit * uk.corpSmallRate
        : profit <= 250000
          ? profit * uk.corpTaxRate - (250000 - profit) * (3 / 200)
          : profit * uk.corpTaxRate;
      const afterCorpTax   = profit - seTax;
      const directorSalary = Math.min(uk.personalAllowance, afterCorpTax);
      const dividendIncome = Math.max(0, afterCorpTax - directorSalary);
      const taxableDivs    = Math.max(0, dividendIncome - uk.divAllowance);
      const totalIncome    = directorSalary + dividendIncome;
      let stateTax = 0;
      if (totalIncome <= uk.basicRateLimit)       stateTax = taxableDivs * uk.divBasicRate;
      else if (totalIncome <= uk.higherRateLimit) {
        const basicPortion  = Math.max(0, uk.basicRateLimit - directorSalary);
        stateTax = (Math.min(taxableDivs, basicPortion) * uk.divBasicRate) + (Math.max(0, taxableDivs - basicPortion) * uk.divHigherRate);
      } else                                      stateTax = taxableDivs * uk.divAddlRate;
      const totalTax      = seTax + stateTax;
      const reserveNeeded = Math.max(0, totalTax - quarterPaid);
      return { federalTax: 0, stateTax, seTax, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: grossIncome > 0 ? Math.round((reserveNeeded / grossIncome) * 100) : 0, underpaymentPenalty: 0, totalTax };
    }

    // ── US ──
    const rawSalary = money(taxInputs.salary);
    const safeGross     = Math.max(0, grossIncome); // guard against negative input
    // Salary can't exceed gross revenue for an S-Corp (IRS requirement)
    const salary        = taxInputs.entityType === "S-Corp" ? Math.min(rawSalary, safeGross) : rawSalary;
    const seTaxBase     = taxInputs.entityType === "1099" ? safeGross * 0.9235 : salary;
    // SS (12.4%) capped at $168,600 wage base; Medicare (2.9%) is uncapped
    // Additional Medicare Tax (0.9%) on earned income above $200k
    const additionalMedicare = Math.max(0, seTaxBase - 200000) * 0.009;
    const seTax         = Math.min(seTaxBase, 168600) * 0.124 + seTaxBase * 0.029 + additionalMedicare;
    const stdDed        = cfg.us.standardDeductions[taxInputs.filingStatus] ?? 14600;
    // Select correct bracket table for filing status
    const fedBrackets   = taxInputs.filingStatus === "Married"
      ? normaliseBrackets(US_MFJ_BRACKETS)
      : taxInputs.filingStatus === "Single with Dependent"
        ? normaliseBrackets(US_HOH_BRACKETS)
        : usFedBrackets;
    // S-Corp: employer FICA (half of payroll tax) reduces K-1 profit at corp level.
    // 1099:   half of SE tax is a personal deduction on Schedule 1.
    // Both equal seTax/2, so the formula is the same; salary cap makes S-Corp coherent.
    const taxableIncome = Math.max(0, safeGross - stdDed - totalDeductions - seTax / 2);
    const federalTax    = calcBracketTax(taxableIncome, fedBrackets);
    const stateTax      = safeGross * (cfg.us.stateRates[taxInputs.state] ?? 0);
    const penalty       = taxInputs.quarterlyEnabled ? 0 : (federalTax + stateTax + seTax) * 0.02;
    const totalTax      = federalTax + stateTax + seTax + penalty;
    const reserveNeeded = Math.max(0, totalTax - quarterPaid);
    return { federalTax, stateTax, seTax, totalDeductions, quarterlyPaid: quarterPaid, reserveNeeded, suggestedReservePct: safeGross > 0 ? Math.round((reserveNeeded / safeGross) * 100) : 0, underpaymentPenalty: penalty, totalTax };
  }, [taxInputs, isCorpEntity, taxConfig, customConfig]);

  // ─── Persist suggested reserve % ─────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem("fh_tax_reserve_pct", String(taxResults.suggestedReservePct));
    if (user && taxResults.suggestedReservePct > 0) {
      (async () => {
        const { error } = await supabase.from('users').update({ tax_reserve_pct: taxResults.suggestedReservePct }).eq('id', user.id);
        if (error) console.error('[taxes] tax_reserve_pct save failed:', error);
      })();
    }
  }, [taxResults.suggestedReservePct, user]);

  // ─── Styles ──────────────────────────────────────────────────────────────
  const pageClasses = isDark ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white" : "bg-[#E3E8F1] text-[#1A1F36]";
  const cardClasses = isDark
    ? "bg-white/10 border-white/10 [@supports_not_(backdrop-filter:blur(1px))]:bg-[#131820]"
    : "bg-white/80 border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white";
  const inputCls    = `w-full px-4 py-3 rounded-2xl border transition-all outline-none appearance-none ${isDark ? "bg-white/5 border-white/10 text-white placeholder-white/20 focus:border-white/30" : "bg-white/50 border-gray-200 text-slate-900 placeholder-slate-300 focus:border-gray-400"}`;
  const labelCls    = `${isDark ? "text-white/40" : "text-[#1A1F36]/40"} text-[10px] font-bold uppercase tracking-[0.2em] mb-2 block`;
  const secondary   = isDark ? "text-white/50" : "text-slate-500";
  const noteCls     = `text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`;
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Country badge label
  const countryBadge: Record<Country, string> = {
    US: "🇺🇸 US", CA: "🇨🇦 CA", UK: "🇬🇧 UK",
    FR: "🇫🇷 FR", DE: "🇩🇪 DE", AU: "🇦🇺 AU", NL: "🇳🇱 NL", CUSTOM: "🌍 Custom", NONE: "—",
  };

  // Result rows for the Live Estimate card (country-specific)
  const resultRows: [string, number][] = useMemo(() => {
    if (taxInputs.country === "FR") return [
      ["Income Tax (after 34% BNC allowance)", taxResults.federalTax],
      ["Social Charges (21.2% on gross)", taxResults.seTax],
    ];
    if (taxInputs.country === "DE") return [
      ["Income Tax (Einkommensteuer)", taxResults.federalTax],
      ...(taxResults.stateTax > 0 ? [["Solidarity Surcharge", taxResults.stateTax] as [string, number]] : []),
      ...(taxResults.totalDeductions > 0 ? [["Business Deductions Applied", taxResults.totalDeductions] as [string, number]] : []),
    ];
    if (taxInputs.country === "CUSTOM") return [
      ["Income Tax", taxResults.federalTax],
      ...(taxResults.seTax > 0       ? [["Social Charges / NI", taxResults.seTax] as [string, number]] : []),
      ...(taxResults.totalDeductions > 0 ? [["Deductions Applied", taxResults.totalDeductions] as [string, number]] : []),
    ];
    if (taxInputs.country === "NL") return [
      ["Income Tax / Box 1 (after MKB & heffingskorting)", taxResults.federalTax],
      ["Zelfstandigenaftrek + Deductions Applied", taxResults.stateTax],
    ];
    if (taxInputs.country === "AU") return isCorpEntity ? [
      ["Company Tax (25%)", taxResults.seTax],
      ...(taxResults.totalDeductions > 0 ? [["Total Deductions Applied", taxResults.totalDeductions] as [string, number]] : []),
      ["Payments Made (PAYG)", taxResults.quarterlyPaid],
    ] : [
      ["Income Tax (after LITO)", taxResults.federalTax],
      ["Medicare Levy (2%)", taxResults.stateTax],
      ...(taxResults.totalDeductions > 0 ? [["Total Deductions Applied", taxResults.totalDeductions] as [string, number]] : []),
      ["Payments Made (PAYG)", taxResults.quarterlyPaid],
    ];
    if (taxInputs.country === "UK") return [
      ["Income Tax (estimated)", taxResults.federalTax],
      ...(isCorpEntity ? [["Dividend Tax", taxResults.stateTax] as [string, number]] : []),
      [seLabel, taxResults.seTax],
      ["Total Deductions Applied", taxResults.totalDeductions],
      ["Payments Made", taxResults.quarterlyPaid],
    ];
    // US / CA
    return [
      ["Federal Tax (estimated)", taxResults.federalTax],
      [stateTaxLabel, taxResults.stateTax],
      [seLabel, taxResults.seTax],
      ["Total Deductions Applied", taxResults.totalDeductions],
      ["Payments Made", taxResults.quarterlyPaid],
    ];
  }, [taxInputs.country, taxResults, isCorpEntity, seLabel, stateTaxLabel]);

  const ChevronIcon = () => (
    <div className={`pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 ${isDark ? "text-white/30" : "text-[#1A1F36]/30"}`}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </div>
  );

  return (
    <div className={`min-h-screen relative overflow-hidden transition-colors duration-300 ${pageClasses}`}>

      {/* Lead gate overlay */}
      {showLeadGate && (
        <div className="fixed inset-0 z-[100] backdrop-blur-md bg-white/70 dark:bg-black/60 flex items-center justify-center overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-white/95 dark:[@supports_not_(backdrop-filter:blur(1px))]:bg-black/85">
          <div className="bg-white dark:bg-white/10 rounded-[2rem] p-8 shadow-xl max-w-sm w-full text-center border border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white">
            <Lock className="w-8 h-8 text-[#4A6CF7] mx-auto mb-4" />
            <h3 className="text-2xl font-black text-[#1A1F36]">Lead Plan Required</h3>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/40 mt-2 mb-6">
              Tax estimation is available on the Lead plan.
            </p>
            <ul className="space-y-2 mb-6 text-left">
              {['Everything in Junior', 'Estimated Taxes & Payouts', 'Advanced Analytics', 'Invoice Management'].map(f => (
                <li key={f} className="flex items-center gap-2 text-xs font-bold text-[#1A1F36]/70">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#4A6CF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {f}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate('/settings#subscription-section')}
              className="bg-black text-white rounded-full font-black uppercase tracking-[0.2em] px-8 py-3 text-[10px] hover:bg-gray-900 transition-colors w-full">
              Upgrade to Lead — $9.50/mo
            </button>
          </div>
        </div>
      )}

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'} ${isDark ? "bg-blue-600/10" : "bg-blue-300/10"}`} />
        <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'} ${isDark ? "bg-purple-600/10" : "bg-purple-300/10"}`} style={{ animationDelay: "1s" }} />
      </div>

      <main className="relative z-10 max-w-[1400px] mx-auto p-4 md:p-8 pt-4 pb-28 md:pb-8">

        {/* Header */}
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 flex justify-between items-start gap-4 md:gap-8">
          <div className="flex-1">
            <h1 className={`text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-2 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Tax Estimator</h1>
            <p className={`ml-3 font-light text-base sm:text-lg md:text-xl ${isDark ? "text-white/50" : "text-[#1A1F36]/50"}`}>Approximate your annual tax liability.</p>
          </div>
          <div className="flex items-center gap-4">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={toggleTheme}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${isDark ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-white/60 text-[#1A1F36] hover:bg-white border border-white/60 shadow-sm"}`}>
              {isDark ? <SunIcon /> : <MoonIcon />}
            </motion.button>
          </div>
        </motion.header>

        <div className={`grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-8 transition-opacity duration-200 ${taxInputsLoaded ? 'opacity-100' : 'opacity-0'}`}>

          {/* ── Left column: inputs ── */}
          <div className="space-y-6">
            <div className={`backdrop-blur-2xl rounded-[2.5rem] border p-5 md:p-8 ${!showNoneCard ? 'min-h-[600px]' : ''} ${cardClasses}`}>

              {/* Country selector */}
              <div className={labelCls}>Country</div>
              <div className="relative mb-6">
                <select className={`${inputCls} pr-10`} value={taxInputs.country}
                  onChange={e => handleCountryChange(e.target.value as Country)}>
                  <option value="US">🇺🇸 United States</option>
                  <option value="CA">🇨🇦 Canada</option>
                  <option value="UK">🇬🇧 United Kingdom</option>
                  <option value="AU">🇦🇺 Australia</option>
                  <option value="NL">🇳🇱 Netherlands</option>
                  <option value="FR">🇫🇷 France</option>
                  <option value="DE">🇩🇪 Germany</option>
                  <option disabled>──────────────</option>
                  <option value="CUSTOM">🌍 Other / Custom</option>
                </select>
                <ChevronIcon />
              </div>

              {/* Entity Type — US/CA/UK only */}
              {showEntityType && (
                <>
                  <div className={labelCls}>Entity Type</div>
                  <div className="flex gap-2 mb-6">
                    {entityLabels.map((label, i) => {
                      const v = i === 0 ? "1099" : "S-Corp";
                      return (
                        <button key={v} onClick={() => setTaxInputs(p => ({ ...p, entityType: v }))}
                          className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                            taxInputs.entityType === v ? "bg-[#1A1F36] text-white"
                            : isDark ? "bg-white/5 text-white/30 hover:text-white/60" : "bg-black/5 text-[#1A1F36]/30 hover:text-[#1A1F36]/60"
                          }`}>{label}</button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Annual Income */}
              {showGrossIncome && (
                <div className="mb-6">
                  <div className="flex items-baseline justify-between">
                    <div className={labelCls}>
                      Projected Annual Income <span className="opacity-50 normal-case tracking-normal font-normal">({currencySymbol})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => { void applyLiveYtdGrossIncome(); }}
                      className={`text-[9px] font-bold uppercase tracking-[0.15em] underline-offset-2 hover:underline ${secondary}`}
                      title="Overwrite with your actual year-to-date booking earnings"
                    >
                      Use live earnings
                    </button>
                  </div>
                  <input className={inputCls} value={taxInputs.grossIncome}
                    onChange={e => setTaxInputs(p => ({ ...p, grossIncome: fmtInput(e.target.value, currencySymbol) }))} />
                  <p className={`text-xs mt-1.5 ${secondary}`}>
                    Type any figure to see the tax impact.
                  </p>
                </div>
              )}

              {/* Salary — US S-Corp only */}
              {taxInputs.country === "US" && taxInputs.entityType === "S-Corp" && (
                <div className="mb-6">
                  <div className={labelCls}>Reasonable Salary</div>
                  <input className={inputCls} value={taxInputs.salary}
                    onChange={e => setTaxInputs(p => ({ ...p, salary: fmtInput(e.target.value, "$") }))} />
                </div>
              )}

              {/* Filing Status — US only */}
              {showFilingStatus && (
                <>
                  <div className={labelCls}>Filing Status</div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {(["Single", "Married", "Single with Dependent"] as const).map(v => (
                      <button key={v} onClick={() => setTaxInputs(p => ({ ...p, filingStatus: v }))}
                        className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                          taxInputs.filingStatus === v ? "bg-[#1A1F36] text-white"
                          : isDark ? "bg-white/5 text-white/30 hover:text-white/60" : "bg-black/5 text-[#1A1F36]/30 hover:text-[#1A1F36]/60"
                        }`}>{v}</button>
                    ))}
                  </div>
                </>
              )}

              {/* State / Province — US/CA only */}
              {showState && (
                <div className="mb-6">
                  <div className={labelCls}>{stateLabel}</div>
                  <div className="relative">
                    <select className={`${inputCls} pr-10`} value={taxInputs.state}
                      onChange={e => setTaxInputs(p => ({ ...p, state: e.target.value }))}>
                      {stateList.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <ChevronIcon />
                  </div>
                </div>
              )}


              {/* ── FR read-only rate info fields ── */}
              {taxInputs.country === 'FR' && (
                <div className="mt-6 space-y-3">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary} mb-3`}>Applied Rates (France BNC)</div>
                  {([
                    { label: "BNC Income Allowance", value: "34% deduction before income tax" },
                    { label: "Income Tax Rate", value: "Progressive — 0% to 45%" },
                    { label: "Social Charges (URSSAF)", value: "21.2% of gross revenue" },
                    { label: "Payment Frequency", value: "Monthly or quarterly (URSSAF election)" },
                  ] as const).map(({ label, value }) => (
                    <div key={label}>
                      <div className={labelCls}>{label}</div>
                      <div className={`${inputCls} opacity-60 cursor-default pointer-events-none flex items-center justify-between`}>
                        <span className="text-sm font-medium">{value}</span>
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ml-3 shrink-0 ${isDark ? 'bg-white/10 text-white/40' : 'bg-black/5 text-black/30'}`}>Fixed</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── NL read-only rate info fields ── */}
              {taxInputs.country === 'NL' && (
                <div className="mt-6 space-y-3">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary} mb-3`}>Applied Rates (Netherlands ZZP)</div>
                  {([
                    { label: "Income Tax (Box 1)", value: "36.97% up to €75,518 · 49.50% above" },
                    { label: "Zelfstandigenaftrek", value: "€3,750 self-employment deduction" },
                    { label: "MKB-winstvrijstelling", value: "13.31% SME profit exemption" },
                    { label: "Algemene Heffingskorting", value: "Up to €3,362 tax credit (income-dependent)" },
                  ] as const).map(({ label, value }) => (
                    <div key={label}>
                      <div className={labelCls}>{label}</div>
                      <div className={`${inputCls} opacity-60 cursor-default pointer-events-none flex items-center justify-between`}>
                        <span className="text-sm font-medium">{value}</span>
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ml-3 shrink-0 ${isDark ? 'bg-white/10 text-white/40' : 'bg-black/5 text-black/30'}`}>Fixed</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── DE read-only rate info fields ── */}
              {taxInputs.country === 'DE' && (
                <div className="mt-6 space-y-3">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary} mb-3`}>Applied Rates (Germany Freiberufler)</div>
                  {([
                    { label: "Income Tax", value: "Progressive — 0% to 45%" },
                    { label: "Basic Allowance (Grundfreibetrag)", value: "€11,604 exempt" },
                    { label: "Solidarity Surcharge", value: "5.5% on income tax (most freelancers exempt)" },
                    { label: "Trade Tax (Gewerbesteuer)", value: "Exempt for Freiberufler" },
                  ] as const).map(({ label, value }) => (
                    <div key={label}>
                      <div className={labelCls}>{label}</div>
                      <div className={`${inputCls} opacity-60 cursor-default pointer-events-none flex items-center justify-between`}>
                        <span className="text-sm font-medium">{value}</span>
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ml-3 shrink-0 ${isDark ? 'bg-white/10 text-white/40' : 'bg-black/5 text-black/30'}`}>Fixed</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── CUSTOM MODE form ── */}
              {showCustomForm && (
                <div className="mt-6 space-y-4">
                  <div className={`my-4 border-t ${isDark ? "border-white/10" : "border-black/5"}`} />
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary}`}>Custom Tax Rates</div>

                  {/* Country name */}
                  <div>
                    <div className={labelCls}>Country Name</div>
                    <input
                      className={inputCls}
                      placeholder="e.g. Italy, Australia, Netherlands"
                      value={customConfig.countryName}
                      onChange={e => setCustomConfig(p => ({ ...p, countryName: e.target.value }))}
                      onBlur={handleCountryNameBlur}
                    />
                  </div>

                  {/* Income tax rate */}
                  <div>
                    <div className={labelCls}>Income Tax Rate %</div>
                    <input
                      className={inputCls}
                      type="text" inputMode="decimal"
                      placeholder="e.g. 30"
                      value={customConfig.incomeTaxRate}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(?=\d)/, '');
                        setCustomConfig(p => ({ ...p, incomeTaxRate: raw }));
                      }}
                    />
                  </div>

                  {/* Social charges */}
                  <div>
                    <div className={labelCls}>Social Charges / NI %</div>
                    <input
                      className={inputCls}
                      type="text" inputMode="decimal"
                      placeholder="e.g. 15 (applied to annual income)"
                      value={customConfig.socialChargesPct}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(?=\d)/, '');
                        setCustomConfig(p => ({ ...p, socialChargesPct: raw }));
                      }}
                    />
                  </div>

                  {/* Standard deduction */}
                  <div>
                    <div className={labelCls}>Standard Deduction Amount</div>
                    <input
                      className={inputCls}
                      placeholder="e.g. 10000"
                      value={customConfig.standardDeduction}
                      onChange={e => setCustomConfig(p => ({ ...p, standardDeduction: fmtInput(e.target.value, currencySymbol) }))}
                    />
                  </div>

                  {/* Additional expenses */}
                  <div>
                    <div className={labelCls}>Additional Expenses Deduction %</div>
                    <input
                      className={inputCls}
                      type="text" inputMode="decimal"
                      placeholder="e.g. 10 (home office, equipment)"
                      value={customConfig.additionalExpensesPct}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9.]/g, '').replace(/^0+(?=\d)/, '');
                        setCustomConfig(p => ({ ...p, additionalExpensesPct: raw }));
                      }}
                    />
                  </div>

                  {/* VAT toggle */}
                  <div className={`rounded-2xl border p-4 ${isDark ? "border-white/10" : "border-black/10"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isDark ? "text-white/40" : "text-[#1A1F36]/40"}`}>VAT Registered</span>
                      <button
                        onClick={() => setCustomConfig(p => ({ ...p, vatRegistered: !p.vatRegistered }))}
                        className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-all hover:scale-110 active:scale-95 ${
                          customConfig.vatRegistered ? "bg-[#4A6CF7] text-white shadow-lg shadow-blue-500/20"
                          : isDark ? "bg-[#3A4154] text-white/60" : "bg-[#DDE1EA] text-[#1A1F36]/60"
                        }`}>
                        {customConfig.vatRegistered ? "On" : "Off"}
                      </button>
                    </div>
                    {customConfig.vatRegistered && (
                      <p className={`mt-3 text-[10px] ${secondary}`}>
                        VAT is separate from income tax. This estimate covers income tax and social charges only.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Deductions — US/CA/UK/DE */}
              {showDeductionUI && (
                <>
                  <button onClick={() => setShowDeductions(v => !v)}
                    className={`mt-6 text-[10px] font-bold uppercase tracking-[0.2em] ${secondary} flex items-center gap-2`}>
                    {showDeductions ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Deductions
                  </button>
                  {showDeductions && (
                    <div className="mt-4 space-y-4">
                      {([
                        ["homeOffice",      "Home Office"],
                        ["equipment",       "Equipment & Software"],
                        ["healthInsurance", "Health Insurance"],
                        ["retirement",
                          taxInputs.country === "CA" ? "RRSP / Retirement" :
                          taxInputs.country === "UK" ? "Pension Contributions" :
                          taxInputs.country === "DE" ? "Retirement / Pension" :
                          "Retirement (Solo 401k / SEP-IRA)"],
                      ] as const).map(([key, label]) => {
                        const d = taxInputs.deductions[key as keyof TaxInputs["deductions"]];
                        return (
                          <div key={key} className={`rounded-2xl border p-4 space-y-3 ${isDark ? "border-white/10" : "border-black/10"}`}>
                            <div className="flex items-center justify-between">
                              <span className={`${isDark ? "text-white/40" : "text-[#1A1F36]/40"} text-[10px] font-bold uppercase tracking-[0.2em]`}>{label}</span>
                              <button
                                onClick={() => setTaxInputs(p => ({ ...p, deductions: { ...p.deductions, [key]: { ...(p.deductions as any)[key], enabled: !d.enabled } } }))}
                                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-all hover:scale-110 active:scale-95 ${
                                  d.enabled ? "bg-[#4A6CF7] text-white shadow-lg shadow-blue-500/20"
                                  : isDark ? "bg-[#3A4154] text-white/60" : "bg-[#DDE1EA] text-[#1A1F36]/60"
                                }`}>
                                {d.enabled ? "On" : "Off"}
                              </button>
                            </div>
                            {d.enabled && key === "homeOffice" && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="relative">
                                  <input className={`${inputCls} pr-8`} placeholder="30" inputMode="decimal" value={(d as typeof taxInputs.deductions.homeOffice).percentage}
                                    onChange={e => setTaxInputs(p => ({ ...p, deductions: { ...p.deductions, homeOffice: { ...p.deductions.homeOffice, percentage: e.target.value.replace(/[^0-9.]/g, '') } } }))} />
                                  <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-sm font-bold pointer-events-none ${isDark ? "text-white/30" : "text-slate-400"}`}>%</span>
                                </div>
                                <input className={inputCls} placeholder={`Monthly rent / mortgage (${currencySymbol})`} value={(d as typeof taxInputs.deductions.homeOffice).monthlyRent}
                                  onChange={e => setTaxInputs(p => ({ ...p, deductions: { ...p.deductions, homeOffice: { ...p.deductions.homeOffice, monthlyRent: fmtInput(e.target.value, currencySymbol) } } }))} />
                              </div>
                            )}
                            {d.enabled && key !== "homeOffice" && (
                              <input className={inputCls} placeholder={`${currencySymbol}0`} value={(d as any).amount}
                                onChange={e => setTaxInputs(p => ({ ...p, deductions: { ...p.deductions, [key]: { ...(p.deductions as any)[key], amount: fmtInput(e.target.value, currencySymbol) } } }))} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {/* Quarterly Payments — US/CA/UK */}
              {showQuarterly && (
                <div className="mt-6">
                  <div className={labelCls}>{taxInputs.country === "UK" ? "Payments on Account" : taxInputs.country === "AU" ? "PAYG Instalments" : "Quarterly Payments"}</div>
                  <div className="flex gap-2">
                    {["Yes", "No"].map(opt => (
                      <button key={opt} onClick={() => setTaxInputs(p => ({ ...p, quarterlyEnabled: opt === "Yes" }))}
                        className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] transition-colors ${
                          (opt === "Yes") === taxInputs.quarterlyEnabled ? "bg-[#1A1F36] text-white"
                          : isDark ? "bg-white/5 text-white/30 hover:text-white/60" : "bg-black/5 text-[#1A1F36]/30 hover:text-[#1A1F36]/60"
                        }`}>{opt}</button>
                    ))}
                  </div>
                  {taxInputs.quarterlyEnabled ? (
                    <div className="mt-6 grid grid-cols-2 gap-3">
                      {(["q1","q2","q3","q4"] as const).map(q => (
                        <input key={q} className={inputCls} placeholder={q.toUpperCase()} value={taxInputs.quarterly[q]}
                          onChange={e => setTaxInputs(p => ({ ...p, quarterly: { ...p.quarterly, [q]: fmtInput(e.target.value, currencySymbol) } }))} />
                      ))}
                    </div>
                  ) : (
                    <p className={`mt-4 text-xs ${secondary}`}>Potential underpayment penalty may apply.</p>
                  )}
                </div>
              )}
            </div>

            {/* Hero image */}
            <div className="rounded-[2.5rem] h-[220px] relative overflow-hidden">
              <img src="https://res.cloudinary.com/dlmbskxuc/image/upload/v1779320465/firsthold_taxes_tlet0d.png"
                alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
            </div>
          </div>

          {/* ── Right column: results or unsupported card ── */}
          <div className="lg:sticky lg:top-8 h-fit">

            {/* ── NONE: unsupported country ── */}
            {showNoneCard ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className={`backdrop-blur-2xl rounded-[2.5rem] border p-5 md:p-10 ${cardClasses}`}>
                <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary} mb-6`}>Not Yet Supported</div>
                <h2 className={`text-2xl font-black mb-3 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>
                  Tax estimation for your country is coming soon.
                </h2>
                <p className={`text-sm leading-relaxed mb-8 ${secondary}`}>
                  In the meantime, you can use our Custom mode to enter your own tax rates manually.
                </p>
                <button
                  onClick={() => handleCountryChange('CUSTOM')}
                  className={`w-full py-3 rounded-full font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:scale-[1.02] active:scale-[0.98] ${isDark ? "bg-white text-[#1A1F36]" : "bg-black text-white"}`}>
                  Use Custom Mode
                </button>
                <p className={`mt-6 text-xs ${secondary}`}>
                  Want us to add your country?{" "}
                  <a href="mailto:hello@firsthold.io" className="underline hover:opacity-70 transition-opacity">
                    hello@firsthold.io
                  </a>
                </p>
              </motion.div>

            ) : (
              /* ── Standard results card ── */
              <div className={`backdrop-blur-2xl rounded-[2.5rem] border p-5 md:p-8 min-h-[600px] flex flex-col justify-between ${cardClasses}`}>
                {/* Header */}
                <div className="flex items-center gap-2 mb-6 flex-wrap">
                  <CircleDollarSign size={16} />
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary}`}>Live Estimate</div>
                  <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${isDark ? "bg-white/10 text-white/30" : "bg-black/5 text-[#1A1F36]/30"}`}>
                    {countryBadge[taxInputs.country]}
                  </span>
                </div>

                {/* Result rows */}
                <div className="space-y-4">
                  {resultRows.map(([label, value]) => (
                    <div key={label} className="flex items-center justify-between gap-4">
                      <span className={`text-sm ${secondary}`}>{label}</span>
                      <span className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1F36]"}`}>{fmt(value)}</span>
                    </div>
                  ))}
                </div>

                <div className={`my-6 border-t ${isDark ? "border-white/10" : "border-black/5"}`} />

                {/* Summary */}
                <div className="space-y-3">
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary}`}>Reserve Needed</div>
                  <div className={`text-5xl font-black ${isDark ? "text-white" : "text-[#1A1F36]"}`}>{fmt(taxResults.reserveNeeded)}</div>
                  <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary}`}>Suggested Reserve %</div>
                  <div className={`text-4xl font-black ${isDark ? "text-blue-400" : "text-blue-600"}`}>{taxResults.suggestedReservePct}%</div>
                  {(taxInputs.country === "FR" || taxInputs.country === "DE" || taxInputs.country === "CUSTOM") && taxResults.totalTax > 0 && (
                    <>
                      <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary}`}>Monthly Set-Aside</div>
                      <div className={`text-2xl font-black ${isDark ? "text-white/70" : "text-[#1A1F36]/70"}`}>{fmt(taxResults.totalTax / 12)}</div>
                    </>
                  )}
                </div>

                {/* Disclaimers */}
                <div className="mt-6">
                  <p className={`text-xs ${secondary}`}>
                    This is an estimate only. Consult a tax professional for filing advice.
                    {taxInputs.country === "US" && isCorpEntity && " S-Corp: salary is capped at annual income if entered higher; FICA (15.3%) on salary, federal tax on remaining K-1 + salary income."}
                    {(taxInputs.country === "US" || taxInputs.country === "CA" || taxInputs.country === "UK") && " Tax estimates use simplified flat state/provincial rates."}
                    {taxInputs.country === "NL" && " ZZP model. Zorgtoeslag, arbeidskorting, and AOV (disability insurance) not included. Consult a boekhouder for full picture."}
                    {taxInputs.country === "AU" && !isCorpEntity && " LITO applied. Medicare Levy Surcharge not included (assumes private health cover). No state income tax in Australia."}
                    {taxInputs.country === "AU" &&  isCorpEntity && " Base rate entity (turnover under A$50M). Dividend imputation and franking credits not modelled."}
                    {taxInputs.country === "FR" && ` ${(taxConfig.fr ?? DEFAULT_FR).notes}`}
                    {taxInputs.country === "DE" && " Freiberufler are exempt from trade tax. Health insurance not included in this estimate."}
                    {taxInputs.country === "CUSTOM" && " Custom mode uses rates you provide."}
                  </p>
                </div>
              </div>
            )}

            {/* ── Request Your Country card (CUSTOM only) ── */}
            {taxInputs.country === "CUSTOM" && (
              <div className={`mt-6 backdrop-blur-2xl rounded-[2.5rem] border p-5 md:p-8 flex flex-col items-center gap-3 text-center ${cardClasses}`}>
                <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondary}`}>Request Tax Support For Your Country</div>
                <p className={`text-sm ${secondary}`}>Let us know which country you'd like added to Firsthold.</p>
                <button
                  onClick={() => navigate('/help')}
                  className="mt-2 px-6 py-2.5 rounded-full bg-[#1A1F36] text-white text-xs font-bold uppercase tracking-[0.2em] hover:scale-105 active:scale-95 transition-transform"
                >
                  Message Us
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function SunIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
